import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuX } from 'react-icons/lu';
import GlassIcon from '../../../../components/ui/GlassIcon';
import { GLASS_CHIP, GLASS_PANEL, GLASS_POPOVER } from '../../../../components/ui/glass';
import { CARD as PROFILE_CARD, TILE } from '../../../ProfilePage/components/ProfileUi';
import { abs } from '../engine/stage';

// Building blocks of the guide's mock screens. They copy the portal's glass
// classes so the video looks like the real pages; geometry comes from rects.

// The profile card's look without its `tw-relative`: on the stage every card
// is absolutely placed, and `relative` would win over `absolute` in the CSS.
export const CARD = PROFILE_CARD.replace('tw-relative ', '');

export { TILE, GLASS_CHIP, GLASS_PANEL, GLASS_POPOVER };

/** An absolutely positioned box at a stage rectangle. */
export const Abs = ({ rect, className = '', style, children }) => (
  <div style={{ ...abs(rect), ...style }} className={`tw-absolute ${className}`}>{children}</div>
);

Abs.propTypes = { rect: PropTypes.object.isRequired, className: PropTypes.string, style: PropTypes.object, children: PropTypes.node };

const BUTTON_BASE = 'tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden tw-whitespace-nowrap tw-rounded-full tw-px-3 tw-text-sm tw-font-semibold tw-transition-all tw-duration-300';
const BUTTON_VARIANTS = {
  primary: 'tw-border tw-border-white/40 tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-lg tw-shadow-teal-500/30 dark:tw-border-white/20',
  success: 'tw-border tw-border-white/40 tw-bg-gradient-to-b tw-from-emerald-400 tw-to-emerald-600 tw-text-white tw-shadow-lg tw-shadow-emerald-500/30',
  secondary: `${GLASS_CHIP} tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none dark:tw-text-slate-200`,
  ghost: 'tw-bg-transparent tw-text-slate-600 dark:tw-text-slate-300',
  danger: 'tw-border tw-border-rose-500/25 tw-bg-rose-500/10 tw-text-rose-700 dark:tw-text-rose-300',
  warning: 'tw-border tw-border-amber-500/30 tw-bg-gradient-to-b tw-from-amber-400 tw-to-orange-500 tw-text-white tw-shadow-lg tw-shadow-amber-500/30',
};

/** A capsule button. `pressed` dims it for a moment (the cursor clicked). */
export const Btn = ({ rect, variant = 'secondary', icon: Icon, pressed, busy, children, className = '' }) => (
  <span style={abs(rect)} className={`${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${pressed ? 'tw-scale-95 tw-brightness-95' : ''} ${busy ? 'tw-opacity-80' : ''} ${className}`}>
    {Icon && <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />}
    {children && <span className="tw-truncate">{children}</span>}
  </span>
);

Btn.propTypes = { rect: PropTypes.object.isRequired, variant: PropTypes.string, icon: PropTypes.elementType, pressed: PropTypes.bool, busy: PropTypes.bool, children: PropTypes.node, className: PropTypes.string };

/** Round glass icon button. */
export const IconBtn = ({ rect, icon: Icon, badge, active, tone, className = '' }) => (
  <span style={abs(rect)} className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none ${active ? 'tw-text-teal-600 dark:tw-text-teal-300' : tone || 'tw-text-slate-600 dark:tw-text-slate-300'} ${className}`}>
    <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
    {badge ? (
      <span className="tw-absolute -tw-right-1 -tw-top-1 tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none tw-text-white tw-ring-2 tw-ring-white dark:tw-ring-[#22262f]">{badge}</span>
    ) : null}
  </span>
);

IconBtn.propTypes = { rect: PropTypes.object.isRequired, icon: PropTypes.elementType.isRequired, badge: PropTypes.node, active: PropTypes.bool, tone: PropTypes.string, className: PropTypes.string };

/** A form control as drawn: label above, value or placeholder, focus ring, optional chevron. */
export const Field = ({ rect, label, value, placeholder, focused, select, required, area, prefix, hint, error, className = '' }) => (
  <div style={abs(rect)} className={`tw-absolute ${className}`}>
    {label && (
      <span className="tw-absolute -tw-top-[22px] tw-left-0.5 tw-whitespace-nowrap tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
        {label}
        {required && <span className="tw-ml-1 tw-text-rose-500">*</span>}
      </span>
    )}
    <span className={`tw-flex tw-h-full tw-w-full tw-gap-2 tw-rounded-xl tw-px-3.5 tw-text-sm ${area ? 'tw-items-start tw-py-2.5' : 'tw-items-center'} ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-bg-none tw-transition-shadow tw-duration-300 ${focused ? 'tw-ring-2 tw-ring-teal-500/50' : ''} ${error ? 'tw-ring-2 tw-ring-rose-500/50' : ''}`}>
      {prefix && <span className="tw-shrink-0 tw-text-slate-400">{prefix}</span>}
      <span className={`tw-min-w-0 tw-flex-1 tw-truncate ${area ? 'tw-whitespace-normal tw-leading-snug' : ''} ${value ? 'tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400'}`}>
        {value || placeholder}
        {focused && !select && <span className="tw-ml-px tw-inline-block tw-h-4 tw-w-px tw-translate-y-0.5 tw-animate-pulse tw-bg-teal-600" />}
      </span>
      {select && <LuChevronDown aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />}
    </span>
    {(hint || error) && (
      <span className={`tw-absolute tw-left-0.5 tw-top-full tw-mt-1 tw-whitespace-nowrap tw-text-[11px] ${error ? 'tw-font-semibold tw-text-rose-600' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>{error || hint}</span>
    )}
  </div>
);

Field.propTypes = {
  rect: PropTypes.object.isRequired,
  label: PropTypes.node,
  value: PropTypes.node,
  placeholder: PropTypes.node,
  focused: PropTypes.bool,
  select: PropTypes.bool,
  required: PropTypes.bool,
  area: PropTypes.bool,
  prefix: PropTypes.node,
  hint: PropTypes.node,
  error: PropTypes.node,
  className: PropTypes.string,
};

/** Section header of a profile tab: icon tile, title, description and an optional action on the right. */
export const SectionHead = ({ top, icon: Icon, gradient, title, description, children }) => (
  <div style={{ left: 256, top, width: 1000 }} className="tw-absolute tw-flex tw-items-start tw-gap-3">
    <GlassIcon gradient={gradient} size="xs">
      <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
    </GlassIcon>
    <div className="tw-min-w-0 tw-flex-1 tw-pr-48">
      <p className="tw-text-lg tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{title}</p>
      {description && <p className="tw-mt-1 tw-line-clamp-2 tw-text-[13px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{description}</p>}
    </div>
    {children}
  </div>
);

SectionHead.propTypes = { top: PropTypes.number.isRequired, icon: PropTypes.elementType.isRequired, gradient: PropTypes.string.isRequired, title: PropTypes.node, description: PropTypes.node, children: PropTypes.node };

/** Segmented switch; option widths are fixed so the cursor can aim at them. */
export const Segmented = ({ rect, options, value, small }) => (
  <span style={abs(rect)} className="tw-absolute tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-slate-900/[0.05] tw-p-1 dark:tw-bg-white/[0.06]">
    {options.map((option) => (
      <span
        key={option.value}
        style={{ width: option.w }}
        className={`tw-flex tw-h-full tw-items-center tw-justify-center tw-gap-1 tw-truncate tw-rounded-full tw-px-2 tw-font-semibold tw-transition-colors tw-duration-300 ${small ? 'tw-text-xs' : 'tw-text-[13px]'} ${
          option.value === value ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm dark:tw-bg-white/15 dark:tw-text-white' : 'tw-text-slate-500 dark:tw-text-slate-400'
        }`}
      >
        {option.label}
      </span>
    ))}
  </span>
);

Segmented.propTypes = { rect: PropTypes.object.isRequired, options: PropTypes.array.isRequired, value: PropTypes.string, small: PropTypes.bool };

/** x positions of segmented options inside `rect` (for aiming the cursor). */
export const segmentRects = (rect, widths) => {
  let x = rect.x + 4;
  return widths.map((w) => {
    const r = { x, y: rect.y + 4, w, h: rect.h - 8 };
    x += w + 4;
    return r;
  });
};

/**
 * A dimmed page with a dialog panel: header (icon, title, subtitle, close)
 * and an optional footer bar. The contents (children) are drawn in stage
 * coordinates on a layer above the panel, so they can use the same rects the
 * timeline aims at. `titleRight` keeps room for header actions.
 */
export const Dialog = ({ rect, title, subtitle, icon, children, footer, appear = 1, dim = true, footerH = 64, titleRight = 0, z = 20 }) => {
  const motion = { opacity: appear, transform: `translateY(${(1 - appear) * 24}px)` };
  return (
    <>
      {dim && <div style={{ opacity: appear, zIndex: z - 10 }} className="tw-absolute tw-inset-0 tw-bg-slate-950/45 dark:tw-bg-black/60" />}
      <div
        style={{ ...abs(rect), ...motion, zIndex: z }}
        className="tw-absolute tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white tw-shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)] dark:tw-border-white/10 dark:tw-bg-[#1f232b]"
      >
        <div className="tw-flex tw-h-[68px] tw-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-slate-200/80 tw-px-5 dark:tw-border-white/10">
          {icon}
          <div style={{ marginRight: titleRight }} className="tw-min-w-0 tw-flex-1">
            <p className="tw-truncate tw-text-[17px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{title}</p>
            {subtitle && <p className="tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">{subtitle}</p>}
          </div>
          <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-slate-500 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none`}>
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
          </span>
        </div>
        <div className="tw-min-h-0 tw-flex-1" />
        {footer && <div style={{ height: footerH }} className="tw-shrink-0 tw-border-t tw-border-slate-200/80 tw-bg-slate-50/80 dark:tw-border-white/10 dark:tw-bg-white/[0.02]" />}
      </div>
      <div style={{ ...motion, zIndex: z + 1 }} className="tw-absolute tw-inset-0">{children}</div>
    </>
  );
};

Dialog.propTypes = {
  rect: PropTypes.object.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.bool,
  appear: PropTypes.number,
  dim: PropTypes.bool,
  footerH: PropTypes.number,
  titleRight: PropTypes.number,
  z: PropTypes.number,
};

/** Small gradient icon used in dialog headers. */
export const DialogIcon = ({ icon: Icon, gradient }) => (
  <GlassIcon gradient={gradient} size="xs">
    <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
  </GlassIcon>
);

DialogIcon.propTypes = { icon: PropTypes.elementType.isRequired, gradient: PropTypes.string.isRequired };

/** Initials on a gradient disc. */
export const Avatar = ({ initials, size = 32, gradient = 'tw-from-teal-400 tw-to-cyan-600', className = '' }) => (
  <span
    style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.36)) }}
    className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${gradient} ${className}`}
  >
    {initials}
  </span>
);

Avatar.propTypes = { initials: PropTypes.node, size: PropTypes.number, gradient: PropTypes.string, className: PropTypes.string };

/** Blue tip note (like ProfileUi HelpNote). */
export const Note = ({ rect, tone = 'info', icon: Icon, children }) => {
  const tones = {
    info: 'tw-bg-sky-500/[0.08] tw-text-sky-900 dark:tw-text-sky-100',
    warning: 'tw-bg-amber-500/10 tw-text-amber-900 dark:tw-text-amber-100',
    danger: 'tw-bg-rose-500/10 tw-text-rose-800 dark:tw-text-rose-200',
    success: 'tw-bg-emerald-500/10 tw-text-emerald-800 dark:tw-text-emerald-200',
  };
  return (
    <div style={abs(rect)} className={`tw-absolute tw-flex tw-items-start tw-gap-2 tw-overflow-hidden tw-rounded-2xl tw-px-3.5 tw-py-2.5 tw-text-[13px] tw-leading-snug ${tones[tone]}`}>
      {Icon && <Icon aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-80" />}
      <div className="tw-min-w-0">{children}</div>
    </div>
  );
};

Note.propTypes = { rect: PropTypes.object.isRequired, tone: PropTypes.string, icon: PropTypes.elementType, children: PropTypes.node };

/** A pulsing teal ring around an element that has just changed. */
export const Flash = ({ rect, show }) => (
  <span style={{ ...abs(rect), opacity: show ? 1 : 0 }} className="tw-pointer-events-none tw-absolute tw-z-[5] tw-rounded-2xl tw-ring-[3px] tw-ring-emerald-400/70 tw-transition-opacity tw-duration-500" />
);

Flash.propTypes = { rect: PropTypes.object.isRequired, show: PropTypes.bool };
